import React, { useEffect, useRef, useState } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { useLocation, useSearchParams } from 'react-router-dom'
import { v4 as uuidv4 } from 'uuid'
import Autocomplete from '@mui/material/Autocomplete'
import Box from '@mui/material/Box'
import Button from '@mui/material/Button'
import CircularProgress from '@mui/material/CircularProgress'
import Paper from '@mui/material/Paper'
import TextField from '@mui/material/TextField'
import Typography from '@mui/material/Typography'
import DownloadIcon from '@mui/icons-material/Download'
import SearchIcon from '@mui/icons-material/Search'
import ErrorBanner from '../../components/shared/ErrorBanner'
import PipelineService from '../../services/pipelineService'
import axiosInstance from '../../services/axios/axiosConfig'
import { endpoints } from '../../services/axios/endpoints'
import { errorMessage } from '../../services/axios/errorMessage'
import { isNoAnswer } from '../../services/axios/withApiError'
import {
    emptyReportFilters,
    reportCsv,
    reportPayload,
    reportRequestProblem,
} from '../../helpers/reports'
import { downloadFile } from '../../utils/download'
import { istDateTimeInput, nowUnix } from '../../utils/datetime'
import { RADIUS, STATUS, SURFACE, TEXT, cardSx } from '../../theme/tokens'
import ReportResults from './ReportResults'

/** How often a large report's file is looked for, and for how long (5 minutes). */
export const POLL_INTERVAL_MS = 5000
export const MAX_POLLS = 60
/** The file link the server hands out is valid for 600 seconds. */
export const LINK_LIFETIME_MS = 600 * 1000

// Every call here shows its own progress and messages: no full-screen
// loader, and no toast repeating what the card already says ("comm log
// found" popped up on every search).
const QUIET = { silentToast: true, silentErrorToast: true, background: true }

/** The steps of a report: nothing yet, a request in flight, waiting for the file, the file ready, or given up. */
const STEP = {
    IDLE: 'idle',
    SEARCHING: 'searching',
    PREPARING: 'preparing',
    READY: 'ready',
    EXPIRED: 'expired',
    FAILED: 'failed',
}

/**
 * The comm-log search: filters, "Search" (rows on the page) and "Download
 * Report" (the same request, saved as a file), and the large-report file
 * flow.
 *
 * A small report (up to one page of logs) comes back in the answer and is
 * shown as a table. A large one is written to a file in the background; the
 * card polls for it and offers a "Download report" button, a click on which
 * opens the link (a link opened from a timer is stopped by popup blockers).
 * When the server gives no answer (the gateway timed out) the job may still
 * be running, so the card keeps polling with the same job id; a real
 * refusal (unknown Lead ID, a bad filter) is shown as the server said it.
 *
 * A Report ID handed over, in navigation state (`location.state.reportId`,
 * from the Bulk Send result) or in the address (`?reportId=`, which the
 * result's link also carries so it can be reloaded or shared), fills the
 * Report ID filter and runs the search at once: a filled filter with no
 * results read as a search that did not work.
 */
const CommLogsCard = () => {
    const dispatch = useDispatch()
    const location = useLocation()
    const [searchParams] = useSearchParams()
    const eventTypes = useSelector((state) => state.pipeline?.eventTypes) ?? []
    const handedReportId =
        location.state?.reportId || searchParams.get('reportId') || ''

    const [filters, setFilters] = useState(() => ({
        ...emptyReportFilters(),
        reportId: handedReportId,
    }))
    const [step, setStep] = useState(STEP.IDLE)
    const [error, setError] = useState('')
    // The last small report: its rows and its job id (names the file).
    const [result, setResult] = useState(null)
    const [preparingNote, setPreparingNote] = useState('')
    const [link, setLink] = useState('')
    const [lastMode, setLastMode] = useState('search')

    const pollRef = useRef(null)
    const expiryRef = useRef(null)
    const mountedRef = useRef(true)
    // The job the card is following. An answer for any other job (one
    // cleared, or replaced by a newer search) is dropped.
    const jobRef = useRef(null)
    const isCurrent = (commId) =>
        mountedRef.current && jobRef.current === commId

    const stopTimers = () => {
        clearInterval(pollRef.current)
        clearTimeout(expiryRef.current)
        pollRef.current = null
        expiryRef.current = null
    }

    useEffect(() => {
        mountedRef.current = true
        if (!eventTypes.length) dispatch(PipelineService.getEventTypes())
        return () => {
            mountedRef.current = false
            stopTimers()
        }
    }, [])

    useEffect(() => {
        if (!handedReportId) return
        const handed = { ...emptyReportFilters(), reportId: handedReportId }
        setFilters(handed)
        run('search', handed)
    }, [handedReportId])

    const setFilter = (key) => (value) =>
        setFilters((prev) => ({ ...prev, [key]: value }))

    const fileName = (commId) => `comm_report_${commId}.csv`

    /** Poll for a large report's file by its job id, then offer it. */
    const waitForFile = (commId) => {
        stopTimers()
        setStep(STEP.PREPARING)
        let polls = 0
        pollRef.current = setInterval(async () => {
            polls += 1
            if (polls > MAX_POLLS) {
                stopTimers()
                if (!isCurrent(commId)) return
                setStep(STEP.FAILED)
                setError('The report file was not ready after 5 minutes.')
                return
            }
            try {
                const resp = await axiosInstance.get(
                    endpoints.getCommReport(commId),
                    QUIET
                )
                const url = resp?.data?.response
                if (!url || !isCurrent(commId)) return
                stopTimers()
                setLink(url)
                setStep(STEP.READY)
                expiryRef.current = setTimeout(() => {
                    if (!isCurrent(commId)) return
                    setLink('')
                    setStep(STEP.EXPIRED)
                }, LINK_LIFETIME_MS)
            } catch (err) {
                // A poll the gateway dropped says nothing about the job: the
                // next one asks again.
                if (isNoAnswer(err) || !isCurrent(commId)) return
                stopTimers()
                setStep(STEP.FAILED)
                setError(
                    `Could not check the report file: ${errorMessage(
                        err.response
                    )}`
                )
            }
        }, POLL_INTERVAL_MS)
    }

    /**
     * Send the report request for `active` (the form's filters unless a
     * handed-over Report ID is searched before the form has them). `mode`
     * 'search' shows a small report's rows; 'download' also saves them as a
     * file. A large report goes to the file flow either way.
     */
    const run = async (mode, active = filters) => {
        const problem = reportRequestProblem(active)
        if (problem) {
            setError(problem)
            return
        }
        stopTimers()
        setLastMode(mode)
        setError('')
        setResult(null)
        setLink('')
        setPreparingNote('')
        setStep(STEP.SEARCHING)
        // The job's id is ours, so a request the gateway gave up on can
        // still be followed to its file.
        const commId = uuidv4()
        jobRef.current = commId
        try {
            const resp = await axiosInstance.post(
                endpoints.getCommReports,
                reportPayload(active, commId),
                QUIET
            )
            if (!isCurrent(commId)) return
            const { report_type: type, response } = resp.data ?? {}
            if (type === 'small_report') {
                const found = Array.isArray(response) ? response : []
                setResult({ rows: found, commId })
                setStep(STEP.IDLE)
                if (mode === 'download' && found.length)
                    downloadFile(
                        reportCsv(found),
                        fileName(commId),
                        'text/csv;charset=utf-8;'
                    )
            } else if (type === 'multipart_upload') {
                setPreparingNote(
                    'This report is too large to show here. It is being prepared as a file, which can take up to 5 minutes.'
                )
                // The server files the report under the id it was sent.
                waitForFile(commId)
            } else {
                setStep(STEP.FAILED)
                setError('The server sent an answer this page cannot read.')
            }
        } catch (err) {
            if (!isCurrent(commId)) return
            if (isNoAnswer(err)) {
                setPreparingNote(
                    'The server did not answer in time. If the report is large it is still being prepared as a file; checking for up to 5 minutes.'
                )
                waitForFile(commId)
                return
            }
            setStep(STEP.IDLE)
            setError(
                `The report was not generated: ${errorMessage(err.response)}`
            )
        }
    }

    const clear = () => {
        stopTimers()
        jobRef.current = null
        setFilters(emptyReportFilters())
        setResult(null)
        setLink('')
        setError('')
        setPreparingNote('')
        setStep(STEP.IDLE)
    }

    const busy = step === STEP.SEARCHING || step === STEP.PREPARING
    const nowInput = istDateTimeInput(nowUnix())

    return (
        <Paper elevation={0} sx={{ ...cardSx, p: 2.5, mb: 3 }}>
            <Typography
                variant="overline"
                component="h2"
                sx={{ color: TEXT.muted, display: 'block', mb: 1.5 }}
            >
                Comm logs
            </Typography>
            <Box
                sx={{
                    display: 'grid',
                    gridTemplateColumns: {
                        xs: 'minmax(0, 1fr)',
                        md: 'repeat(3, minmax(0, 1fr))',
                    },
                    gap: 1.5,
                    mb: 2,
                }}
            >
                <TextField
                    label="Report ID"
                    value={filters.reportId}
                    onChange={(e) => setFilter('reportId')(e.target.value)}
                    helperText="The event's registration id"
                />
                <TextField
                    label="Lead ID"
                    value={filters.leadId}
                    onChange={(e) => setFilter('leadId')(e.target.value)}
                />
                <TextField
                    label="Email"
                    value={filters.email}
                    onChange={(e) => setFilter('email')(e.target.value)}
                    placeholder="student@example.com"
                />
                <TextField
                    label="Mobile No"
                    value={filters.mobileNo}
                    onChange={(e) => setFilter('mobileNo')(e.target.value)}
                    placeholder="+91-9876543210"
                    helperText="As stored: plus, country code, hyphen, number"
                />
                <Autocomplete
                    freeSolo
                    options={eventTypes}
                    inputValue={filters.eventType}
                    onInputChange={(_, value) => setFilter('eventType')(value)}
                    renderInput={(params) => (
                        <TextField
                            {...params}
                            label="Event Type"
                            placeholder="e.g. demo_booked"
                        />
                    )}
                />
                <Box />
                <TextField
                    label="From (India time)"
                    type="datetime-local"
                    value={filters.start}
                    onChange={(e) => setFilter('start')(e.target.value)}
                    InputLabelProps={{ shrink: true }}
                    inputProps={{ max: nowInput }}
                />
                <TextField
                    label="To (India time)"
                    type="datetime-local"
                    value={filters.end}
                    onChange={(e) => setFilter('end')(e.target.value)}
                    InputLabelProps={{ shrink: true }}
                    inputProps={{
                        max: nowInput,
                        ...(filters.start && { min: filters.start }),
                    }}
                />
            </Box>
            <Typography variant="caption" sx={{ color: TEXT.muted }}>
                Search needs a Report ID, Lead ID, Email, Mobile No or Event
                Type; the date range only narrows it.
            </Typography>
            <Box
                sx={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: 1.5,
                    mt: 1.5,
                    flexWrap: 'wrap',
                }}
            >
                <Button
                    variant="contained"
                    disabled={busy}
                    onClick={() => run('search')}
                    startIcon={<SearchIcon />}
                >
                    Search
                </Button>
                <Button
                    variant="outlined"
                    disabled={busy}
                    onClick={() => run('download')}
                    startIcon={<DownloadIcon />}
                >
                    Download Report
                </Button>
                <Button variant="outlined" onClick={clear}>
                    Clear
                </Button>
                {step === STEP.SEARCHING && (
                    <Box
                        role="status"
                        sx={{ display: 'flex', alignItems: 'center', gap: 1 }}
                    >
                        <CircularProgress size={14} />
                        <Typography
                            variant="body2"
                            sx={{ color: TEXT.secondary }}
                        >
                            Searching...
                        </Typography>
                    </Box>
                )}
            </Box>

            {error && (
                <Box sx={{ mt: 2 }}>
                    <ErrorBanner message={error} />
                </Box>
            )}

            {step === STEP.PREPARING && (
                <Box
                    role="status"
                    sx={{
                        mt: 2,
                        p: 1.5,
                        display: 'flex',
                        alignItems: 'center',
                        gap: 1.5,
                        border: `1px solid ${SURFACE.border}`,
                        borderRadius: `${RADIUS.md}px`,
                    }}
                >
                    <CircularProgress size={16} />
                    <Typography variant="body2" sx={{ color: TEXT.secondary }}>
                        {preparingNote}
                    </Typography>
                </Box>
            )}

            {step === STEP.READY && (
                <Box
                    role="status"
                    sx={{
                        mt: 2,
                        p: 1.5,
                        display: 'flex',
                        alignItems: 'center',
                        gap: 2,
                        flexWrap: 'wrap',
                        border: `1px solid ${SURFACE.border}`,
                        borderRadius: `${RADIUS.md}px`,
                    }}
                >
                    <Box sx={{ flex: 1, minWidth: 220 }}>
                        <Typography
                            variant="subtitle2"
                            sx={{ color: STATUS.success.fg }}
                        >
                            Download ready
                        </Typography>
                        <Typography variant="body2" sx={{ color: TEXT.muted }}>
                            The link expires in 10 minutes.
                        </Typography>
                    </Box>
                    <Button
                        variant="contained"
                        component="a"
                        href={link}
                        target="_blank"
                        rel="noopener noreferrer"
                        startIcon={<DownloadIcon />}
                    >
                        Download report
                    </Button>
                </Box>
            )}

            {(step === STEP.FAILED || step === STEP.EXPIRED) && (
                <Box
                    sx={{
                        mt: 2,
                        display: 'flex',
                        alignItems: 'center',
                        gap: 1.5,
                        flexWrap: 'wrap',
                    }}
                >
                    {step === STEP.EXPIRED && (
                        <Typography
                            variant="body2"
                            sx={{ color: TEXT.secondary }}
                        >
                            The download link has expired.
                        </Typography>
                    )}
                    <Button variant="outlined" onClick={() => run(lastMode)}>
                        Try again
                    </Button>
                </Box>
            )}

            {result && result.rows.length === 0 && (
                <Typography
                    role="status"
                    variant="body2"
                    sx={{ color: TEXT.secondary, mt: 2 }}
                >
                    No comm logs match these filters.
                </Typography>
            )}
            {result && result.rows.length > 0 && (
                <ReportResults
                    rows={result.rows}
                    onDownload={() =>
                        downloadFile(
                            reportCsv(result.rows),
                            fileName(result.commId),
                            'text/csv;charset=utf-8;'
                        )
                    }
                />
            )}
        </Paper>
    )
}

export default CommLogsCard
